import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, ArrowUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X, Upload, FileSpreadsheet, CheckCircle, AlertTriangle, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { isAdmin as isAdminRole, canViewCosts as canViewCostsRole } from '@/lib/roles';
import { parseCSV } from '@/lib/csv';
import { formatCurrency, cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useListNavigationStore } from '@/lib/useListNavigation';
import { fifoReallocate } from '@/lib/fifoReallocate';
import { mapSkusToTokens, matchingTokenIndices, type BomTokenMatch } from '@/lib/buildComponentLookup';
import { useCurrentUser } from '@/lib/useCurrentUser';
import * as XLSX from 'xlsx';

type SortField = 'part_number' | 'description' | 'category' | 'supplier' | 'available_qty' | 'reserved_qty' | 'on_order_qty' | 'current_price' | 'status' | 'build_match';
type SortOrder = 'asc' | 'desc';

const COL_WIDTHS_KEY = 'components.colWidths.v1';
const COL_WIDTH_DEFAULTS: Record<string, number> = {
  part_number: 150,
  description: 320,
  build_match: 160,
  category: 130,
  supplier: 150,
  available_qty: 110,
  reserved_qty: 110,
  on_order_qty: 110,
  current_price: 110,
  status: 130,
};

import { getComponentSortRank } from '@/types/database';
import { HelpTooltip } from '@/components/HelpTooltip';

const getCategoryRank = (cat: string | null | undefined): number => {
  if (!cat) return 999;
  return getComponentSortRank(cat);
};

// Map category text → component_type code for specs lookup
const CATEGORY_CODE_MAP: Record<string, string> = {
  cpu: 'CPU',
  fan: 'FAN', cooler: 'FAN', coo: 'COO',
  mb: 'MB', mobo: 'MB', motherboard: 'MB',
  ram: 'RAM', memory: 'RAM',
  ssd: 'STORAGE', hdd: 'STORAGE', rom: 'STORAGE', storage: 'STORAGE', nvme: 'STORAGE',
  gpu: 'GPU', vga: 'GPU', graphics: 'GPU',
  psu: 'PSU', power: 'PSU',
  cas: 'CASE', case: 'CASE', chassis: 'CASE', tower: 'CASE', enclosure: 'CASE',
  os: 'OS', windows: 'OS',
  acc: 'ACC',
};

const getTypeCode = (category: string | null | undefined): string | null => {
  if (!category) return null;
  const lower = category.toLowerCase();
  for (const [prefix, code] of Object.entries(CATEGORY_CODE_MAP)) {
    if (lower.startsWith(prefix)) return code;
  }
  return null;
};

// Ordered category display config: [db_value_prefix, label]
const CATEGORY_DISPLAY: [string, string][] = [
  ['CPU', 'CPU'],
  ['FAN', 'CPU Cooler'],
  ['RAM', 'RAM'],
  ['MB', 'Motherboard'],
  ['ROM', 'Storage'],
  ['GPU', 'Graphics'],
  ['PSU', 'PSU'],
  ['OS', 'OS'],
  ['CAS', 'Case'],
  ['COO', 'Case Cooling'],
  ['ACC', 'Accessories'],
];

const getCategoryLabel = (cat: string): string => {
  const upper = cat.toUpperCase();
  const entry = CATEGORY_DISPLAY.find(([prefix]) => upper.startsWith(prefix));
  return entry ? entry[1] : cat;
};

const getCategoryDisplayOrder = (cat: string): number => {
  const upper = cat.toUpperCase();
  const idx = CATEGORY_DISPLAY.findIndex(([prefix]) => upper.startsWith(prefix));
  return idx === -1 ? 999 : idx;
};

const TOKEN_PALETTE = [
  { pill: 'bg-blue-100 text-blue-700 border border-blue-300',        rowBg: 'bg-blue-50',     borderHex: '#93c5fd' },
  { pill: 'bg-emerald-100 text-emerald-700 border border-emerald-300', rowBg: 'bg-emerald-50', borderHex: '#6ee7b7' },
  { pill: 'bg-violet-100 text-violet-700 border border-violet-300',   rowBg: 'bg-violet-50',   borderHex: '#c4b5fd' },
  { pill: 'bg-orange-100 text-orange-700 border border-orange-300',   rowBg: 'bg-orange-50',   borderHex: '#fdba74' },
  { pill: 'bg-pink-100 text-pink-700 border border-pink-300',         rowBg: 'bg-pink-50',     borderHex: '#f9a8d4' },
  { pill: 'bg-teal-100 text-teal-700 border border-teal-300',         rowBg: 'bg-teal-50',     borderHex: '#5eead4' },
  { pill: 'bg-rose-100 text-rose-700 border border-rose-300',         rowBg: 'bg-rose-50',     borderHex: '#fda4af' },
  { pill: 'bg-amber-100 text-amber-700 border border-amber-300',      rowBg: 'bg-amber-50',    borderHex: '#fcd34d' },
];
const getTokenPalette = (idx: number) => TOKEN_PALETTE[idx % TOKEN_PALETTE.length];

// Stable empty result, so the filter memo does not rerun on every render while
// the build-token lookup is still in flight.
const EMPTY_TOKEN_SKUS: BomTokenMatch = { tokensBySku: new Map(), skus: [] };

/**
 * Resolve build tokens to the component SKUs their BOMs contain.
 *
 * `components.build_sku` is a legacy column that nothing has written since an
 * early import, so filtering on it alone silently hides every build created
 * since. `build_components` is the live join table; this query is what makes
 * newer builds findable, and the legacy column is kept as a fallback for rows
 * that predate it.
 */
function useBuildTokenSkus(tokens: string[]) {
  const tokenKey = tokens.join(',');

  return useQuery({
    queryKey: ['build-token-skus', tokenKey],
    enabled: tokens.length > 0,
    queryFn: async () => {
      // PostgREST parses or() as a delimited expression, so the punctuation it
      // uses as syntax has to go before a token is interpolated into a filter.
      // An ilike with no trailing % is an exact, case-insensitive match.
      const filters = tokens.flatMap(token => {
        const isWildcard = token.endsWith('*');
        const clean = (isWildcard ? token.slice(0, -1) : token).replace(/[(),]/g, '').trim();
        if (!clean) return [];
        return [isWildcard ? `sku.ilike.${clean}%` : `sku.ilike.${clean}`];
      });
      if (!filters.length) return EMPTY_TOKEN_SKUS;

      const { data: builds, error: buildErr } = await supabase
        .from('builds')
        .select('id, sku')
        .or(filters.join(','));
      if (buildErr) throw buildErr;
      if (!builds?.length) return EMPTY_TOKEN_SKUS;

      const { data: bom, error: bomErr } = await supabase
        .from('build_components')
        .select('build_id, component_sku')
        .in('build_id', builds.map((b: any) => b.id));
      if (bomErr) throw bomErr;

      return mapSkusToTokens(tokens, builds as any, (bom ?? []) as any);
    },
  });
}

// ── Build Token Dropdown Input ────────────────────────────────────────────────
interface BuildTokenInputProps {
  tokens: string[];
  onAdd: (token: string) => void;
  onRemove: (token: string) => void;
  onClearAll: () => void;
}

function BuildTokenInput({ tokens, onAdd, onRemove, onClearAll }: BuildTokenInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');          // debounced version driving the query
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce query state so we don't hammer the DB on every keypress
  useEffect(() => {
    const t = setTimeout(() => setQuery(inputValue.trim()), 180);
    return () => clearTimeout(t);
  }, [inputValue]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { data: suggestions = [] } = useQuery({
    queryKey: ['build-suggestions', query],
    queryFn: async () => {
      if (!query) return [];
      const { data } = await supabase
        .from('builds')
        .select('id, sku, description, status')
        .ilike('sku', `%${query}%`)
        .order('sku')
        .limit(12);
      return data ?? [];
    },
    enabled: !!query,
    staleTime: 30_000,
  });

  const addToken = (token: string) => {
    const normalised = token.trim();
    if (!normalised || tokens.includes(normalised)) return;
    onAdd(normalised);
    setInputValue('');
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !inputValue && tokens.length > 0) {
      onRemove(tokens[tokens.length - 1]);
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setInputValue('');
    }
    // Enter picks the top suggestion or adds as wildcard
    if (e.key === 'Enter') {
      e.preventDefault();
      if (suggestions.length > 0) {
        addToken(suggestions[0].sku);
      } else if (inputValue.trim()) {
        addToken(inputValue.trim() + '*');
      }
    }
  };

  const showDropdown = open && (suggestions.length > 0 || !!query);

  return (
    <div ref={containerRef} className="relative flex-1 min-w-[260px]">
      {/* Tag-style container */}
      <div className="flex items-start rounded-md border border-input bg-background min-h-10">
        {/* Pills + input — wraps freely */}
        <div
          className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0 px-3 py-1.5 cursor-text"
          onClick={() => inputRef.current?.focus()}
        >
          {tokens.map((t, idx) => {
            const pal = getTokenPalette(idx);
            return (
              <span key={t} className={cn('inline-flex items-center gap-1 text-xs font-mono font-semibold uppercase px-2 py-0.5 rounded-full shrink-0', pal.pill)}>
                {t}
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); onRemove(t); }}
                  className="hover:opacity-70 leading-none"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}

          <input
            ref={inputRef}
            value={inputValue}
            onChange={e => { setInputValue(e.target.value); setOpen(true); }}
            onFocus={() => { if (inputValue) setOpen(true); }}
            onKeyDown={handleKeyDown}
            placeholder={tokens.length === 0 ? 'Filter by build SKU…' : 'Add another build…'}
            className="flex-1 min-w-[120px] text-sm bg-transparent outline-none placeholder:text-muted-foreground py-0.5"
          />
        </div>

        {/* Clear-all — anchored to top-right, outside the wrapping area */}
        {(tokens.length > 0 || inputValue) && (
          <button
            type="button"
            className="p-2 mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
            title="Clear all build filters"
            onClick={e => {
              e.stopPropagation();
              setInputValue('');
              setQuery('');
              setOpen(false);
              onClearAll();
            }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md overflow-hidden">
          {suggestions.length === 0 && query && (
            <div className="px-3 py-2 text-sm text-muted-foreground">No builds matching "{query}"</div>
          )}
          {suggestions.map((build: any) => (
            <button
              key={build.id}
              type="button"
              onMouseDown={e => { e.preventDefault(); addToken(build.sku); }}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent transition-colors',
                tokens.includes(build.sku) && 'opacity-40 cursor-default'
              )}
              disabled={tokens.includes(build.sku)}
            >
              <span className="font-mono font-medium shrink-0">{build.sku}</span>
              <span className="text-muted-foreground truncate">{build.description}</span>
              <Badge className={cn('ml-auto shrink-0 text-xs',
                build.status?.toLowerCase() === 'active' ? 'bg-green-100 text-green-700' :
                build.status?.toLowerCase() === 'eol'    ? 'bg-orange-100 text-orange-700' :
                'bg-gray-100 text-gray-600'
              )}>
                {build.status}
              </Badge>
            </button>
          ))}

          {/* "All …*" wildcard option */}
          {query && (
            <button
              type="button"
              onMouseDown={e => { e.preventDefault(); addToken(query + '*'); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent border-t transition-colors text-muted-foreground"
            >
              <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">*</span>
              All builds containing
              <span className="font-mono font-semibold text-foreground">"{query}"</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── CSV Parser ────────────────────────────────────────────────────────────────

type ImportRow = {
  partNumber: string;
  bcAvailable: number;
  bcRawInventory: number;
  bcOnOrder: number;
  bcCost: number | null;
  dbId: string | null;
  dbAvailable: number | null;
  dbOnOrder: number | null;
  dbCost: number | null;
  dbTotalDemand: number;
  matched: boolean;
  availChanged: boolean;
  orderChanged: boolean;
  costChanged: boolean;
};

type ImportStep = 'upload' | 'preview' | 'applying' | 'done';

// Plain async function, not a `new Promise(async ...)` wrapper. The wrapper
// had no way to reject: a corrupt workbook throwing inside XLSX.read left the
// promise pending forever, so the import dialog hung on "parsing" instead of
// reporting a bad file - and the caller's `catch` around parseFile could never
// fire. Returning the async function lets the throw surface, which is what the
// caller was already written to expect.
async function parseFile(file: File): Promise<string[][]> {
  const isExcel = /\.xlsx?$/i.test(file.name);
  if (!isExcel) {
    return parseCSV(await file.text());
  }

  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, defval: '' })
    .map((r: any) => r.map((c: any) => String(c ?? '').trim()));
}

function BCImportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<ImportStep>('upload');
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [result, setResult] = useState<{ updated: number; skipped: number; notFound: number }>({ updated: 0, skipped: 0, notFound: 0 });

  const reset = useCallback(() => {
    setStep('upload');
    setImportRows([]);
    setFileNames([]);
    setParseErrors([]);
    setResult({ updated: 0, skipped: 0, notFound: 0 });
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const allParsed: ImportRow[] = [];
    const names: string[] = [];
    const errors: string[] = [];

    for (const file of Array.from(files)) {
      names.push(file.name);
      let rows: string[][];
      try {
        rows = await parseFile(file);
      } catch {
        errors.push(`${file.name}: failed to parse`);
        continue;
      }

      if (rows.length < 2) {
        errors.push(`${file.name}: empty or invalid`);
        continue;
      }

      const header = rows[0].map(h => h.toLowerCase().replace(/[^a-z0-9_]/g, ''));

      // Find column indices — Item Card report: No., Inventory, Qty. on Purch. Order, Unit Cost
      let colNo = header.findIndex(h => h === 'no' || h === 'no_');
      const colInventory = header.findIndex(h => h === 'inventory' || (h.includes('inventory') && h.includes('item')));
      const colOnOrder = header.findIndex(h => h.includes('qtyonpurchorder') || (h.includes('qty') && h.includes('purchorder')));
      const colCost = header.findIndex(h => h === 'unitcost' || h === 'unit_cost');

      if (colNo === -1) colNo = 0;

      const dataRows = rows.slice(1).filter(r => r[colNo]?.trim());
      for (const r of dataRows) {
        const cost = colCost !== -1 ? (parseFloat(r[colCost] || '0') || 0) : null;
        const bcInv = colInventory !== -1 ? Math.round(parseFloat(r[colInventory] || '0') || 0) : 0;
        allParsed.push({
          partNumber: r[colNo].trim(),
          bcAvailable: bcInv,
          bcRawInventory: bcInv,
          bcOnOrder: colOnOrder !== -1 ? Math.round(parseFloat(r[colOnOrder] || '0') || 0) : 0,
          bcCost: cost != null ? Math.round(cost * 100) / 100 : null,
          dbId: null,
          dbAvailable: null,
          dbOnOrder: null,
          dbCost: null,
          dbTotalDemand: 0,
          matched: false,
          availChanged: false,
          orderChanged: false,
          costChanged: false,
        });
      }
    }

    if (allParsed.length === 0) {
      toast.error('No valid data found in uploaded files');
      setParseErrors(errors);
      return;
    }

    // De-duplicate by part number (last file wins)
    const deduped = new Map<string, ImportRow>();
    for (const row of allParsed) {
      deduped.set(row.partNumber, row);
    }
    const uniqueRows = Array.from(deduped.values());

    // Fetch all matching components from DB — batch in chunks of 500 for large sets
    const partNumbers = uniqueRows.map(r => r.partNumber);
    const dbMap = new Map<string, any>();
    for (let i = 0; i < partNumbers.length; i += 500) {
      const chunk = partNumbers.slice(i, i + 500);
      const { data } = await supabase
        .from('components')
        .select('id, part_number, total_qty, available_qty, on_order_qty, reserved_qty, expected_qty, current_price')
        .in('part_number', chunk);
      for (const c of data ?? []) {
        dbMap.set(c.part_number, c);
      }
    }

    // Enrich rows with DB data
    // BC "Inventory" = total physical stock = available + reserved (not expected)
    // Reconcile: total_demand = reserved + expected, then split based on physical stock
    const enriched = uniqueRows.map(r => {
      const db = dbMap.get(r.partNumber);
      const totalDemand = (db?.reserved_qty ?? 0) + (db?.expected_qty ?? 0);
      const physicallyBacked = Math.min(totalDemand, r.bcAvailable);
      const effectiveAvailable = Math.max(r.bcAvailable - physicallyBacked, 0);
      const dbCost = db?.current_price ?? null;
      return {
        ...r,
        bcRawInventory: r.bcAvailable,
        bcAvailable: effectiveAvailable,
        dbId: db?.id ?? null,
        dbTotalDemand: totalDemand,
        dbAvailable: db?.available_qty ?? null,
        dbOnOrder: db?.on_order_qty ?? null,
        dbCost,
        matched: !!db,
        availChanged: db ? db.total_qty !== r.bcRawInventory : false,
        orderChanged: db ? db.on_order_qty !== r.bcOnOrder : false,
        costChanged: db && r.bcCost != null
          // Don't override existing cost with 0 when item is on order but not available
          ? (r.bcCost === 0 && (dbCost ?? 0) > 0 && effectiveAvailable <= 0 && r.bcOnOrder > 0 ? false : dbCost !== r.bcCost)
          : false,
      };
    });

    setImportRows(enriched);
    setFileNames(names);
    setParseErrors(errors);
    setStep('preview');
  };

  // fifoReallocate is imported from @/lib/fifoReallocate

  const applyChanges = async () => {
    setStep('applying');
    const toUpdate = importRows.filter(r => r.matched && (r.availChanged || r.orderChanged || r.costChanged));
    let updated = 0;
    let skipped = 0;

    for (const row of toUpdate) {
      // Reconcile reserved/expected based on new physical inventory
      const totalDemand = row.dbTotalDemand;
      const newReservedQty = Math.min(totalDemand, row.bcRawInventory);
      const newExpectedQty = totalDemand - newReservedQty;

      const updateData: Record<string, any> = {
        total_qty: row.bcRawInventory,
        available_qty: row.bcAvailable,
        on_order_qty: row.bcOnOrder,
        reserved_qty: newReservedQty,
        expected_qty: Math.max(0, newExpectedQty),
      };
      if (row.bcCost != null && row.costChanged) {
        updateData.current_price = row.bcCost;
      }
      const { error } = await supabase
        .from('components')
        .update(updateData)
        .eq('id', row.dbId!);

      if (error) {
        skipped++;
      } else {
        updated++;
      }
    }

    // FIFO reallocate for all updated SKUs, plus any unchanged SKUs that still
    // carry expected_qty > 0 — these can have stale allocations from reservations
    // created between imports where the BC quantity hadn't changed.
    const updatedSkus = toUpdate.map(r => r.partNumber);
    const unchangedMatchedSkus = importRows
      .filter(r => r.matched && !r.availChanged && !r.orderChanged && !r.costChanged)
      .map(r => r.partNumber);
    const staleExpectedSkus: string[] = [];
    for (let i = 0; i < unchangedMatchedSkus.length; i += 500) {
      const { data: staleRows } = await supabase
        .from('components')
        .select('part_number')
        .in('part_number', unchangedMatchedSkus.slice(i, i + 500))
        .gt('expected_qty', 0);
      staleExpectedSkus.push(...(staleRows || []).map((r: any) => r.part_number));
    }
    const skusToReallocate = [...new Set([...updatedSkus, ...staleExpectedSkus])];
    await fifoReallocate(skusToReallocate);

    // For SKUs whose cost changed, update unit_cost on reservation_components
    // rows that are still unfulfilled (expected_qty > 0). Fully-allocated rows
    // (expected_qty = 0) keep their historical cost unchanged.
    const costChangedRows = toUpdate.filter(r => r.costChanged && r.bcCost != null);
    for (const row of costChangedRows) {
      await supabase
        .from('reservation_components')
        .update({ unit_cost: row.bcCost })
        .eq('component_sku', row.partNumber)
        .gt('expected_qty', 0);
    }

    const notFound = importRows.filter(r => !r.matched).length;
    setResult({ updated, skipped, notFound });
    setStep('done');

    queryClient.invalidateQueries({ queryKey: ['components'] });
    queryClient.invalidateQueries({ queryKey: ['components-cs'] });
  };

  const matched = importRows.filter(r => r.matched);
  const changed = importRows.filter(r => r.matched && (r.availChanged || r.orderChanged));
  const unchanged = importRows.filter(r => r.matched && !r.availChanged && !r.orderChanged);
  const notFound = importRows.filter(r => !r.matched);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Import BC Inventory
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm space-y-3">
              <p className="font-medium">Export 3 Item Card reports from BC and upload them together:</p>

              <p className="text-muted-foreground text-xs">For each category: <strong>Items</strong> &gt; <strong>Omit 0 Values</strong> &gt; filter by Item Category Code &gt; <strong>Edit in Excel</strong> &gt; save as .xlsx</p>

              <div className="space-y-2">
                <p className="font-medium text-xs uppercase tracking-wide text-primary">1. PC Components</p>
                <p className="text-muted-foreground text-xs ml-2">Item Category Code: <strong>PC Components</strong></p>
              </div>

              <div className="space-y-2">
                <p className="font-medium text-xs uppercase tracking-wide text-primary">2. System Build Licenses</p>
                <p className="text-muted-foreground text-xs ml-2">Item Category Code: <strong>SYS BUILD LICENSES</strong></p>
              </div>

              <div className="space-y-2">
                <p className="font-medium text-xs uppercase tracking-wide text-primary">3. Gaming Accessories (Sharkoon)</p>
                <p className="text-muted-foreground text-xs ml-2">Item Category Code: <strong>Gaming Accessories</strong>, Brand: <strong>SHARKOON</strong></p>
              </div>

              <p className="text-muted-foreground text-xs pt-1 border-t">
                Matches <strong>No.</strong> to part numbers. Updates
                <strong> Available Qty</strong> (Inventory),
                <strong> On Order</strong> (Qty. on Purch. Order), and
                <strong> Unit Cost</strong>.
                Upload all 3 files at once.
              </p>
            </div>

            <div className="flex items-center justify-center">
              <label className="flex flex-col items-center gap-2 cursor-pointer border-2 border-dashed rounded-lg p-8 hover:bg-muted/30 transition-colors w-full">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm font-medium">Click to upload files</span>
                <span className="text-xs text-muted-foreground">CSV or Excel (.xlsx) — select multiple files at once</span>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.txt,.xlsx,.xls"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4">
            {/* Files loaded */}
            <div className="text-xs text-muted-foreground">
              Files: {fileNames.map((n, i) => (
                <Badge key={i} variant="outline" className="text-xs mr-1">{n}</Badge>
              ))}
            </div>

            {parseErrors.length > 0 && (
              <div className="text-xs text-red-600 bg-red-50 rounded p-2">
                {parseErrors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}

            {/* Summary badges */}
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-blue-100 text-blue-700 text-sm px-3 py-1">
                {importRows.length} rows total
              </Badge>
              <Badge className="bg-green-100 text-green-700 text-sm px-3 py-1">
                {matched.length} matched
              </Badge>
              <Badge className="bg-yellow-100 text-yellow-700 text-sm px-3 py-1">
                {changed.length} to update
              </Badge>
              <Badge className="bg-gray-100 text-gray-600 text-sm px-3 py-1">
                {unchanged.length} unchanged
              </Badge>
              {notFound.length > 0 && (
                <Badge className="bg-red-100 text-red-700 text-sm px-3 py-1">
                  {notFound.length} not in DB
                </Badge>
              )}
            </div>

            {/* Changes table */}
            {changed.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Changes to apply:</p>
                <div className="max-h-64 overflow-y-auto border rounded-md">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-muted">
                      <tr>
                        <th className="text-left p-2">Part Number</th>
                        <th className="text-right p-2">Avail (old)</th>
                        <th className="text-right p-2">Avail (new)</th>
                        <th className="text-right p-2">Order (old)</th>
                        <th className="text-right p-2">Order (new)</th>
                        <th className="text-right p-2">Cost (old)</th>
                        <th className="text-right p-2">Cost (new)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changed.map(r => (
                        <tr key={r.partNumber} className="border-t">
                          <td className="p-2 font-mono">{r.partNumber}</td>
                          <td className={cn('p-2 text-right', r.availChanged && 'text-red-500 line-through')}>{r.dbAvailable}</td>
                          <td className={cn('p-2 text-right', r.availChanged && 'text-green-600 font-medium')}>{r.bcAvailable}</td>
                          <td className={cn('p-2 text-right', r.orderChanged && 'text-red-500 line-through')}>{r.dbOnOrder}</td>
                          <td className={cn('p-2 text-right', r.orderChanged && 'text-green-600 font-medium')}>{r.bcOnOrder}</td>
                          <td className={cn('p-2 text-right', r.costChanged && 'text-red-500 line-through')}>{r.dbCost != null ? formatCurrency(r.dbCost) : '—'}</td>
                          <td className={cn('p-2 text-right', r.costChanged && 'text-green-600 font-medium')}>{r.bcCost != null ? formatCurrency(r.bcCost) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {changed.length === 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground p-4 bg-muted/30 rounded-lg">
                <CheckCircle className="h-4 w-4 text-green-600" />
                All matched components are already up to date. Nothing to change.
              </div>
            )}

            {/* Unmatched items (collapsible) */}
            {notFound.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5 text-yellow-600 inline" />
                  {notFound.length} items not found in DB (click to expand)
                </summary>
                <div className="mt-2 max-h-32 overflow-y-auto text-xs font-mono text-muted-foreground bg-muted/30 rounded p-2">
                  {notFound.map(r => r.partNumber).join(', ')}
                </div>
              </details>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={reset}>Back</Button>
              <Button onClick={applyChanges} disabled={changed.length === 0}>
                Apply {changed.length} Updates
              </Button>
            </div>
          </div>
        )}

        {step === 'applying' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            <p className="text-sm text-muted-foreground">Updating components...</p>
          </div>
        )}

        {step === 'done' && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3 py-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
              <p className="text-lg font-medium">Import Complete</p>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Badge className="bg-green-100 text-green-700 text-sm px-3 py-1">
                {result.updated} updated
              </Badge>
              {result.skipped > 0 && (
                <Badge className="bg-red-100 text-red-700 text-sm px-3 py-1">
                  {result.skipped} failed
                </Badge>
              )}
              {result.notFound > 0 && (
                <Badge className="bg-gray-100 text-gray-600 text-sm px-3 py-1">
                  {result.notFound} not in DB
                </Badge>
              )}
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={() => { reset(); onOpenChange(false); }}>Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export function ComponentsPage() {
  const [importOpen, setImportOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: currentUser } = useCurrentUser();
  const isAdmin = isAdminRole(currentUser?.role);
  const canViewCosts = canViewCostsRole(currentUser?.role);

  // Component types with specs_schema for advanced search
  const { data: componentTypes } = useQuery({
    queryKey: ['component-types'],
    queryFn: async () => {
      const { data } = await supabase
        .from('component_types')
        .select('id, code, name, sort_order, specs_schema')
        .order('sort_order');
      return data ?? [];
    },
  });

  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [specFilters, setSpecFilters] = useState<Record<string, string>>({});

  const search         = searchParams.get('q')      || '';
  const buildParamRaw  = searchParams.get('build')  || '';
  const categoryFilter = searchParams.get('cat')    || '';
  const statusFilter   = searchParams.get('status') || '';
  const page           = parseInt(searchParams.get('p') || '1', 10);
  const pageSizeRaw    = searchParams.get('size');
  const pageSize: number | 'all' = pageSizeRaw === 'all' ? 'all' : parseInt(pageSizeRaw || '25', 10);
  const sortField      = (searchParams.get('sort')  || 'part_number') as SortField;
  const sortOrder      = (searchParams.get('order') || 'asc') as SortOrder;
  const omitZeroQty    = searchParams.get('omit0') === '1';

  // Token list lives in URL: comma-separated build SKUs / wildcards
  const buildTokens = buildParamRaw.split(',').map(s => s.trim()).filter(Boolean);

  // Local state for part/description search input — updates URL with debounce
  const [searchRaw, setSearchRaw] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (!searchRaw) next.delete('q'); else next.set('q', searchRaw);
        next.set('p', '1');
        return next;
      }, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [searchRaw, setSearchParams]);

  const setParam = (key: string, value: string | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (!value) next.delete(key); else next.set(key, value);
      next.set('p', '1');
      return next;
    }, { replace: true });
  };

  const setPageParam = (p: number) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('p', String(p));
      return next;
    }, { replace: true });
  };

  const handleSort = (field: SortField) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('sort', field);
      next.set('order', sortField === field && sortOrder === 'asc' ? 'desc' : 'asc');
      next.set('p', '1');
      return next;
    }, { replace: true });
  };


  // Build token management
  const addBuildToken = (token: string) => {
    const next = [...buildTokens, token];
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('build', next.join(','));
      p.set('p', '1');
      return p;
    }, { replace: true });
  };

  const removeBuildToken = (token: string) => {
    const next = buildTokens.filter(t => t !== token);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (next.length) p.set('build', next.join(','));
      else p.delete('build');
      p.set('p', '1');
      return p;
    }, { replace: true });
  };

  const clearAllBuildTokens = () => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.delete('build');
      p.set('p', '1');
      return p;
    }, { replace: true });
  };

  // Resolve active spec fields for the current category filter
  const activeSpecFields = useMemo(() => {
    if (!categoryFilter || !componentTypes) return [];
    const code = getTypeCode(categoryFilter);
    if (!code) return [];
    const typeDef = componentTypes.find((t: any) => t.code === code);
    return typeDef?.specs_schema?.fields ?? [];
  }, [categoryFilter, componentTypes]);

  const hasSpecFilters = Object.values(specFilters).some(v => v !== '');

  // Clear spec filters when category changes
  useEffect(() => {
    setSpecFilters({});
  }, [categoryFilter]);

  // Client-side mode: build filter, category sort, build_match sort, or spec filters
  const needsClientSide = buildTokens.length > 0 || sortField === 'category' || sortField === 'build_match' || hasSpecFilters;

  // Component SKUs reachable from the selected builds' BOMs, by token index.
  const { data: buildTokenSkusData, isPending: buildSkusPending } = useBuildTokenSkus(buildTokens);
  const buildTokenSkus = buildTokenSkusData ?? EMPTY_TOKEN_SKUS;
  // react-query reports a disabled query as pending, so only treat it as
  // loading when tokens are actually selected - otherwise the table would
  // never leave its loading state.
  const buildSkusLoading = buildTokens.length > 0 && buildSkusPending;

  // component_categories is a DISTINCT view, not the components table. Reading
  // the table meant fetching one column of every row and de-duplicating here,
  // which silently lost everything past PostgREST's 1000-row cap - and because
  // that scan was ordered by category, what it lost was the alphabetical tail:
  // ROM, the code behind "Storage", stopped appearing in this dropdown once the
  // table outgrew the cap.
  const { data: categories } = useQuery({
    queryKey: ['component-categories'],
    queryFn: async () => {
      const { data } = await supabase
        .from('component_categories')
        .select('category')
        .order('category');
      const unique = (data || []).map((r: any) => r.category).filter(Boolean) as string[];
      return unique.sort((a, b) => getCategoryDisplayOrder(a) - getCategoryDisplayOrder(b));
    },
  });

  const specFilterKey = JSON.stringify(specFilters);
  // The BOM-derived SKUs feed the server-side pre-filter, so they belong in the
  // key - otherwise the fetch would be cached against a stale set.
  const bomSkuKey = buildTokenSkus.skus.join(',');
  const queryKey = needsClientSide
    ? ['components-cs', search, categoryFilter, statusFilter, buildParamRaw, bomSkuKey, sortField, sortOrder, omitZeroQty, specFilterKey]
    : ['components', search, categoryFilter, statusFilter, page, pageSize, sortField, sortOrder, omitZeroQty];

  const { data: rawData, isLoading } = useQuery({
    queryKey,
    // Don't fetch with an empty SKU list while the lookup is still resolving:
    // the pre-filter would exclude the BOM-only rows and cache the result.
    enabled: buildTokens.length === 0 || !buildSkusLoading,
    queryFn: async () => {
      const cols = 'id, part_number, description, category, supplier, available_qty, reserved_qty, on_order_qty, current_price, status, build_sku'
        + (hasSpecFilters ? ', specs' : '');
      let query = supabase
        .from('components')
        .select(cols, { count: 'exact' });

      const trimmedSearch = search.trim();
      if (trimmedSearch)  query = query.or(`part_number.ilike.%${trimmedSearch}%,description.ilike.%${trimmedSearch}%`);
      if (categoryFilter) query = query.eq('category', categoryFilter);
      if (statusFilter)   query = query.eq('status', statusFilter);
      if (omitZeroQty)    query = query.gt('available_qty', 0);

      // Server-side pre-filter over BOTH sources. Filtering on build_sku alone
      // would drop exactly the rows this filter exists to find - a component
      // reachable only through build_components has an empty legacy column -
      // but dropping the pre-filter entirely would fetch the whole table and
      // risk truncation against PostgREST's row cap. So widen it instead.
      if (buildTokens.length > 0) {
        const clauses = buildTokens.map(t => `build_sku.ilike.%${t.replace(/\*$/, '')}%`);

        // A part number containing a double quote would break out of the
        // quoted in() list; there are none in practice, and the client-side
        // pass below still catches any such row.
        const skus = buildTokenSkus.skus.filter(s => !s.includes('"'));
        if (skus.length) clauses.push(`part_number.in.(${skus.map(s => `"${s}"`).join(',')})`);

        query = query.or(clauses.join(','));
      }

      if (sortField !== 'category' && sortField !== 'build_match') {
        query = query.order(sortField, { ascending: sortOrder === 'asc', nullsFirst: false });
      }

      if (!needsClientSide && pageSize !== 'all') {
        const from = (page - 1) * (pageSize as number);
        query = query.range(from, from + (pageSize as number) - 1);
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return { data: data ?? [], serverCount: count ?? 0 };
    },
  });

  const { processedRows, totalCount } = useMemo(() => {
    const rows = rawData?.data ?? [];

    type AnnotatedRow = typeof rows[number] & { _matchingTokens: number[] };

    let filtered: AnnotatedRow[];

    if (buildTokens.length > 0) {
      filtered = rows
        .map((c: any) => {
          const matchingTokens = matchingTokenIndices(buildTokens, c, buildTokenSkus.tokensBySku);
          if (!matchingTokens.length) return null;
          return { ...c, _matchingTokens: matchingTokens };
        })
        .filter(Boolean) as AnnotatedRow[];
    } else {
      filtered = rows.map((c: any) => ({ ...c, _matchingTokens: [] })) as AnnotatedRow[];
    }

    // Apply spec filters — components without specs are always included
    if (hasSpecFilters) {
      const allActive = Object.entries(specFilters).filter(([, v]) => v !== '');
      const exactFilters = allActive.filter(([key]) => !key.endsWith('_min') && !key.endsWith('_max'));
      const rangeFilters = allActive.filter(([key]) => key.endsWith('_min') || key.endsWith('_max'));

      if (exactFilters.length > 0) {
        filtered = filtered.filter((c: any) => {
          const specs = c.specs as Record<string, any> | null;
          if (!specs || Object.keys(specs).length === 0) return true;
          return exactFilters.every(([key, val]) => {
            const specVal = specs[key];
            if (specVal === undefined || specVal === null || specVal === '') return true;
            const field = activeSpecFields.find((f: any) => f.key === key);
            if (field?.type === 'boolean') return String(specVal) === val;
            if (Array.isArray(specVal)) return specVal.some((sv: any) => String(sv).toLowerCase().includes(val.toLowerCase()));
            return String(specVal).toLowerCase().includes(val.toLowerCase());
          });
        });
      }

      if (rangeFilters.length > 0) {
        filtered = filtered.filter((c: any) => {
          const specs = c.specs as Record<string, any> | null;
          if (!specs || Object.keys(specs).length === 0) return true;
          return rangeFilters.every(([key, val]) => {
            const isMin = key.endsWith('_min');
            const baseKey = key.replace(/_(min|max)$/, '');
            const specVal = specs[baseKey];
            if (specVal === undefined || specVal === null || specVal === '') return true;
            const num = Number(specVal);
            const filterNum = Number(val);
            if (isNaN(num) || isNaN(filterNum)) return true;
            return isMin ? num >= filterNum : num <= filterNum;
          });
        });
      }
    }

    let sorted = filtered;
    if (sortField === 'category') {
      sorted = [...filtered].sort((a: any, b: any) => {
        const ra = getCategoryRank(a.category);
        const rb = getCategoryRank(b.category);
        if (ra !== rb) return sortOrder === 'asc' ? ra - rb : rb - ra;
        return (a.category || '').localeCompare(b.category || '');
      });
    } else if (sortField === 'build_match') {
      sorted = [...filtered].sort((a: any, b: any) => {
        const ra = a._matchingTokens[0] ?? 999;
        const rb = b._matchingTokens[0] ?? 999;
        if (ra !== rb) return sortOrder === 'asc' ? ra - rb : rb - ra;
        return (a.part_number || '').localeCompare(b.part_number || '');
      });
    }

    const total = needsClientSide ? sorted.length : (rawData?.serverCount ?? 0);

    let pageRows = sorted;
    if (needsClientSide && pageSize !== 'all') {
      const from = (page - 1) * (pageSize as number);
      pageRows = sorted.slice(from, from + (pageSize as number));
    }

    return { processedRows: pageRows, totalCount: total };
  }, [rawData, buildTokens, buildTokenSkus, sortField, sortOrder, page, pageSize, needsClientSide, specFilters, hasSpecFilters, activeSpecFields]);

  // Store visible IDs for prev/next navigation on detail page
  useListNavigationStore('components', processedRows.map((c: any) => c.id));

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalCount / (pageSize as number));
  const showBuildColumn = buildTokens.length > 0;

  // Resizable column widths (persisted to localStorage)
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COL_WIDTHS_KEY) || '{}');
      return { ...COL_WIDTH_DEFAULTS, ...saved };
    } catch {
      return { ...COL_WIDTH_DEFAULTS };
    }
  });

  const startResize = useCallback((key: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[key] ?? COL_WIDTH_DEFAULTS[key] ?? 100;
    const onMove = (ev: PointerEvent) => {
      const next = Math.max(60, startW + (ev.clientX - startX));
      setColWidths(prev => ({ ...prev, [key]: next }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setColWidths(prev => {
        try { localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(prev)); } catch {}
        return prev;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [colWidths]);

  const totalTableWidth = useMemo(() => {
    let sum = colWidths.part_number + colWidths.description + colWidths.category
      + colWidths.available_qty + colWidths.reserved_qty + colWidths.on_order_qty + colWidths.status;
    if (showBuildColumn) sum += colWidths.build_match;
    if (canViewCosts) sum += colWidths.supplier + colWidths.current_price;
    return sum;
  }, [colWidths, showBuildColumn, canViewCosts]);

  const SortHeader = ({ field, children, className = '' }: { field: SortField; children: React.ReactNode; className?: string }) => (
    <th
      className={cn('relative text-left p-3 font-medium cursor-pointer hover:bg-muted/70 select-none', className)}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1 pr-2 overflow-hidden">
        <span className="truncate">{children}</span>
        <ArrowUpDown className={`h-3 w-3 flex-shrink-0 ${sortField === field ? 'text-primary' : 'text-muted-foreground'}`} />
      </div>
      <span
        onPointerDown={e => startResize(field, e)}
        onClick={e => e.stopPropagation()}
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60"
      />
    </th>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Components</h1>
          <p className="text-muted-foreground">
            {buildTokens.length > 0
              ? `${totalCount} components matching build filter`
              : `${totalCount} components in inventory`}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />Import BC Inventory <HelpTooltip tipKey="components.bcImport" />
            </Button>
            <Button asChild>
              <Link to="/components/new">
                <Plus className="mr-2 h-4 w-4" />Add Component
              </Link>
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-3 items-start">
            {/* Part / description search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search part number or description…"
                className="pl-9 pr-9"
                value={searchRaw}
                onChange={e => setSearchRaw(e.target.value)}
              />
              {searchRaw && (
                <button onClick={() => setSearchRaw('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Build token dropdown input */}
            <BuildTokenInput
              tokens={buildTokens}
              onAdd={addBuildToken}
              onRemove={removeBuildToken}
              onClearAll={clearAllBuildTokens}
            />

            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={categoryFilter}
              onChange={e => setParam('cat', e.target.value || null)}
            >
              <option value="">All Categories</option>
              {(categories ?? []).map(cat => (
                <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
              ))}
            </select>

            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={statusFilter}
              onChange={e => setParam('status', e.target.value || null)}
            >
              <option value="">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Discontinued">Discontinued</option>
            </select>

            <div className="flex items-center gap-4 ml-auto">
              <label className="flex items-center gap-2 cursor-pointer select-none h-10">
                <input
                  type="checkbox"
                  checked={omitZeroQty}
                  onChange={e => setParam('omit0', e.target.checked ? '1' : null)}
                  className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">Omit 0 qty</span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Show:</span>
                <select
                  className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={pageSize}
                  onChange={e => setParam('size', e.target.value)}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Search */}
      {categoryFilter && activeSpecFields.length > 0 && (
        <Card>
          <button
            type="button"
            className="w-full flex items-center gap-2 px-6 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setAdvancedOpen(o => !o)}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Advanced Search
            {hasSpecFilters && (
              <Badge variant="secondary" className="text-xs ml-1">
                {Object.values(specFilters).filter(v => v !== '').length} active
              </Badge>
            )}
            <ChevronDown className={cn('h-4 w-4 ml-auto transition-transform', advancedOpen && 'rotate-180')} />
          </button>
          {advancedOpen && (
            <CardContent className="pt-0 pb-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {activeSpecFields.map((field: any) => {
                  if (field.type === 'boolean') {
                    const val = specFilters[field.key] ?? '';
                    return (
                      <div key={field.key} className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
                        <select
                          className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                          value={val}
                          onChange={e => setSpecFilters(prev => ({ ...prev, [field.key]: e.target.value }))}
                        >
                          <option value="">Any</option>
                          <option value="true">Yes</option>
                          <option value="false">No</option>
                        </select>
                      </div>
                    );
                  }
                  if (field.type === 'select' || field.type === 'multi_select') {
                    const val = specFilters[field.key] ?? '';
                    return (
                      <div key={field.key} className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
                        <select
                          className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                          value={val}
                          onChange={e => setSpecFilters(prev => ({ ...prev, [field.key]: e.target.value }))}
                        >
                          <option value="">Any</option>
                          {(field.options ?? []).map((opt: string) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    );
                  }
                  if (field.type === 'number') {
                    const minVal = specFilters[`${field.key}_min`] ?? '';
                    const maxVal = specFilters[`${field.key}_max`] ?? '';
                    return (
                      <div key={field.key} className="space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
                        <div className="flex gap-1">
                          <input
                            type="number"
                            placeholder="Min"
                            className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                            value={minVal}
                            onChange={e => setSpecFilters(prev => ({ ...prev, [`${field.key}_min`]: e.target.value }))}
                          />
                          <input
                            type="number"
                            placeholder="Max"
                            className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                            value={maxVal}
                            onChange={e => setSpecFilters(prev => ({ ...prev, [`${field.key}_max`]: e.target.value }))}
                          />
                        </div>
                      </div>
                    );
                  }
                  // text field — free text search
                  const val = specFilters[field.key] ?? '';
                  return (
                    <div key={field.key} className="space-y-1">
                      <label className="text-xs font-medium text-muted-foreground">{field.label}</label>
                      <input
                        type="text"
                        placeholder={`Filter ${field.label.toLowerCase()}…`}
                        className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                        value={val}
                        onChange={e => setSpecFilters(prev => ({ ...prev, [field.key]: e.target.value }))}
                      />
                    </div>
                  );
                })}
              </div>
              {hasSpecFilters && (
                <button
                  type="button"
                  className="mt-3 text-xs text-muted-foreground hover:text-foreground underline"
                  onClick={() => setSpecFilters({})}
                >
                  Clear all spec filters
                </button>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {(isLoading || buildSkusLoading) ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="text-sm table-fixed" style={{ width: totalTableWidth, minWidth: '100%' }}>
                  <colgroup>
                    <col style={{ width: colWidths.part_number }} />
                    <col style={{ width: colWidths.description }} />
                    {showBuildColumn && <col style={{ width: colWidths.build_match }} />}
                    <col style={{ width: colWidths.category }} />
                    {canViewCosts && <col style={{ width: colWidths.supplier }} />}
                    <col style={{ width: colWidths.available_qty }} />
                    <col style={{ width: colWidths.reserved_qty }} />
                    <col style={{ width: colWidths.on_order_qty }} />
                    {canViewCosts && <col style={{ width: colWidths.current_price }} />}
                    <col style={{ width: colWidths.status }} />
                  </colgroup>
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <SortHeader field="part_number">Part Number</SortHeader>
                      <SortHeader field="description">Description</SortHeader>
                      {showBuildColumn && <SortHeader field="build_match">Build Match</SortHeader>}
                      <SortHeader field="category">Category</SortHeader>
                      {canViewCosts && <SortHeader field="supplier">Supplier</SortHeader>}
                      <SortHeader field="available_qty" className="text-right">Available</SortHeader>
                      <SortHeader field="reserved_qty" className="text-right">Reserved <HelpTooltip tipKey="components.reserved" /></SortHeader>
                      <SortHeader field="on_order_qty" className="text-right">On Order <HelpTooltip tipKey="components.expected" /></SortHeader>
                      {canViewCosts && <SortHeader field="current_price" className="text-right">Price</SortHeader>}
                      <SortHeader field="status" className="text-center">Status <HelpTooltip tipKey="components.status" /></SortHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {processedRows.map((c: any) => {
                      const avail = c.available_qty ?? 0;
                      const isOut = avail <= 0;
                      const matchingTokens: number[] = c._matchingTokens ?? [];
                      const primaryPal = matchingTokens.length > 0 ? getTokenPalette(matchingTokens[0]) : null;

                      return (
                        <tr
                          key={c.id}
                          className={cn('border-b transition-colors', primaryPal ? primaryPal.rowBg : 'hover:bg-muted/50')}
                        >
                          <td
                            className="p-3"
                            style={primaryPal
                              ? { borderLeft: `3px solid ${primaryPal.borderHex}` }
                              : { borderLeft: '3px solid transparent' }
                            }
                          >
                            <Link to={`/components/${c.id}`} className="font-mono text-xs text-primary hover:underline whitespace-nowrap">
                              {c.part_number}
                            </Link>
                          </td>
                          <td className="p-3 overflow-hidden">
                            <p className="truncate" title={c.description}>{c.description || '—'}</p>
                          </td>
                          {showBuildColumn && (
                            <td className="p-3">
                              <div className="flex flex-wrap gap-1">
                                {matchingTokens.map(idx => {
                                  const pal = getTokenPalette(idx);
                                  return (
                                    <span key={idx} className={cn('text-xs font-mono font-semibold uppercase px-1.5 py-0.5 rounded-full', pal.pill)}>
                                      {buildTokens[idx]}
                                    </span>
                                  );
                                })}
                              </div>
                            </td>
                          )}
                          <td className="p-3">
                            <Badge variant="outline" className="text-xs whitespace-nowrap">{c.category || '—'}</Badge>
                          </td>
                          {canViewCosts && <td className="p-3 text-sm text-muted-foreground whitespace-nowrap">{c.supplier || '—'}</td>}
                          <td className={cn('p-3 text-right font-medium', isOut ? 'text-red-600' : avail < 5 ? 'text-yellow-600' : '')}>
                            {avail}
                          </td>
                          <td className="p-3 text-right text-muted-foreground">
                            {(c.reserved_qty ?? 0) > 0 ? (
                              <Link
                                to={`/reservations?component=${encodeURIComponent(c.part_number)}`}
                                className="text-blue-600 hover:underline font-medium"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {c.reserved_qty}
                              </Link>
                            ) : (
                              0
                            )}
                          </td>
                          <td className="p-3 text-right text-blue-600">{c.on_order_qty ?? 0}</td>
                          {canViewCosts && (
                            <td className="p-3 text-right font-mono text-xs whitespace-nowrap">
                              {c.current_price != null ? formatCurrency(c.current_price) : '—'}
                            </td>
                          )}
                          <td className="p-3 text-center">
                            <Badge className={cn('text-xs',
                              c.status === 'Discontinued' ? 'bg-gray-100 text-gray-600' :
                              c.status === 'Inactive' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            )}>
                              {c.status || '—'}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {!processedRows.length && (
                  <div className="flex items-center justify-center h-32 text-muted-foreground">
                    {buildTokens.length > 0 ? 'No components found for these build SKUs' : 'No components found'}
                  </div>
                )}
              </div>

              {pageSize !== 'all' && totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {((page - 1) * (pageSize as number)) + 1}–{Math.min(page * (pageSize as number), totalCount)} of {totalCount}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" onClick={() => setPageParam(1)} disabled={page === 1}>
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => setPageParam(page - 1)} disabled={page === 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-3 text-sm">Page {page} of {totalPages}</span>
                    <Button variant="outline" size="icon" onClick={() => setPageParam(page + 1)} disabled={page === totalPages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => setPageParam(totalPages)} disabled={page === totalPages}>
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              {pageSize === 'all' && totalCount > 0 && (
                <div className="px-4 py-3 border-t text-sm text-muted-foreground">
                  Showing all {totalCount} components
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <BCImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
}
